import { useEffect, useState, type FocusEvent, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastAction } from '@/components/ui/ToastAction';
import {
  useBottomToastStore,
  type BottomToastLane,
} from '@/stores/bottomToastStore';
import { useOverlayStore } from '@/stores/overlayStore';

/**
 * The one bottom toast slot (YAR-476). Mounted once, in AppShell.
 *
 * Shows `action ?? status` from `bottomToastStore` — never both, never
 * stacked — and owns everything that is the slot's rather than a toast's:
 *
 * - Placement: fixed, 16px above the safe area, the screen less the 20px
 *   page margins (384px max from the desktop breakpoint, per Figma). A
 *   bottom-pinned bar that must stay visible (Review v0's StickyConfirmBar)
 *   sets `--bottom-toast-offset` on the root to its height and the slot
 *   rises by that much; unset, it is 0.
 * - The visible-time clock: pauses on hover, on keyboard focus inside the
 *   toast, while the tab is hidden, and while any Modal / FullScreenSheet /
 *   drawer is open. An overlay also HIDES the toast — it is drawn above
 *   z-[200] overlays, so leaving it up would cover a sheet's footer.
 * - An action toast's `expiresAt`: a wall-clock deadline that ends it
 *   whatever the visible-time clock says (paused, hidden or not), checked
 *   again when the tab comes back since a background tab's timers lag.
 * - Leaving the screen ends an action toast (the task stays done).
 * - Announcement: a polite live region that is never focused. It carries the
 *   message only, so screen readers hear "[Task Name] completed" rather than
 *   the button labels too.
 * - Motion: slides up 12px and fades in over 200ms; fade only under
 *   prefers-reduced-motion.
 */
export function BottomToastSlot() {
  const action = useBottomToastStore((s) => s.action);
  const status = useBottomToastStore((s) => s.status);
  const dismissAction = useBottomToastStore((s) => s.dismissAction);
  const dismissStatus = useBottomToastStore((s) => s.dismissStatus);
  const overlayOpen = useOverlayStore((s) => s.openCount > 0);
  const { pathname } = useLocation();

  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pageHidden, setPageHidden] = useState(
    typeof document !== 'undefined' && document.hidden,
  );

  const lane: BottomToastLane | null = action
    ? 'action'
    : status
      ? 'status'
      : null;
  const entryId = action?.id ?? status?.id ?? null;
  const revision = action?.revision ?? status?.revision ?? null;
  const paused = hovered || focused || overlayOpen || pageHidden;

  // A replaced toast is a new element; a stale hover/focus flag from the
  // old one must not hold the new clock.
  useEffect(() => {
    setHovered(false);
    setFocused(false);
  }, [entryId]);

  useEffect(() => {
    const onVisibility = () => setPageHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Leaving the screen ends the action toast. Status toasts are app-wide.
  useEffect(() => {
    const current = useBottomToastStore.getState().action;
    if (current && current.originPath !== pathname) {
      dismissAction(current.id, 'route');
    }
  }, [pathname, dismissAction]);

  // The visible-time clock. Runs only while the entry is on screen and not
  // paused; on stop it writes back what is left, so a status toast hidden by
  // an undo toast resumes from where it was rather than from the top.
  useEffect(() => {
    if (!lane || !entryId || revision == null || paused) return;
    const entry = useBottomToastStore.getState()[lane];
    const remaining = entry?.remainingMs;
    if (remaining == null) return; // no duration: stays until dismissed
    const startedAt = Date.now();
    const timer = setTimeout(() => {
      if (lane === 'action') dismissAction(entryId, 'timeout');
      else dismissStatus(entryId, 'timeout');
    }, remaining);
    return () => {
      clearTimeout(timer);
      useBottomToastStore
        .getState()
        .setRemaining(
          lane,
          entryId,
          revision,
          Math.max(0, remaining - (Date.now() - startedAt)),
        );
    };
  }, [lane, entryId, revision, paused, dismissAction, dismissStatus]);

  // The hard deadline. Never reads `paused`: hover, focus or an open sheet
  // must not keep an Undo up past the point the server would refuse it.
  // `pageHidden` is a dependency so a tab coming back re-checks at once.
  const actionId = action?.id;
  const expiresAt = action?.expiresAt;
  useEffect(() => {
    if (!actionId || expiresAt == null) return;
    const left = expiresAt - Date.now();
    if (left <= 0) {
      dismissAction(actionId, 'timeout');
      return;
    }
    const timer = setTimeout(() => dismissAction(actionId, 'timeout'), left);
    return () => clearTimeout(timer);
  }, [actionId, expiresAt, pageHidden, dismissAction]);

  const announcement = action
    ? `${action.name} ${action.suffix}`
    : (status?.announcement ?? '');

  let content: ReactNode = null;
  if (action) {
    content = (
      <ToastAction
        name={action.name}
        suffix={action.suffix}
        actionLabel={action.actionLabel}
        onAction={() => {
          dismissAction(action.id, 'action');
          action.onAction();
        }}
        onClose={
          action.closeable === false
            ? undefined
            : () => dismissAction(action.id, 'close')
        }
      />
    );
  } else if (status) {
    content = status.render();
  }

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>
      {content && !overlayOpen && entryId && (
        <div className="pointer-events-none fixed inset-x-5 bottom-[calc(1rem+env(safe-area-inset-bottom)+var(--bottom-toast-offset,0px))] z-[400] flex justify-center">
          <SlotEntry
            key={entryId}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={(e) => {
              // Focus moving between Undo and ✕ stays "inside".
              const next = e.relatedTarget;
              if (!(next instanceof Node && e.currentTarget.contains(next))) {
                setFocused(false);
              }
            }}
          >
            {content}
          </SlotEntry>
        </div>
      )}
    </>
  );
}

interface SlotEntryProps {
  children: ReactNode;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: (e: FocusEvent<HTMLDivElement>) => void;
}

/** Keyed per toast, so a replacement plays the enter motion again. */
function SlotEntry({ children, ...handlers }: SlotEntryProps) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      data-testid="bottom-toast"
      className={[
        'pointer-events-auto w-full md:max-w-[384px]',
        // Reduced motion keeps the fade and drops the travel: the transform
        // still flips, but with no transition it lands while still at 0
        // opacity, so nothing is seen to move.
        'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-opacity',
        shown ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
      ].join(' ')}
      {...handlers}
    >
      {children}
    </div>
  );
}
